import React, { useEffect, useState } from "react";
import './css/NewCSVData.css';

function NewCSVData(props){
    const [dataToShow, setDataToShow] = useState([]);

    const isPopupOpen = props.isPopupOpen;
    const headers = props.headerInfo.headers;
    const headerInfo = props.headerInfo;
    const oldData  = props.oldData;
    // newData is now an object: { validRacers: [], removedRacers: [] } or null
    const newDataObj = props.newData;
    const closeWindow = props.closeWindow;
    const setCsvData = props.setCsvData;
    const callerContext = props.callerContext; // 'FIS' or 'USSA'

    const acceptChanged = () =>{
        // Use validRacers from the new data structure
        const validRacers = newDataObj?.validRacers || [];
        console.log('Accepting changes with:', validRacers);
        setCsvData(JSON.parse(JSON.stringify(validRacers)));
        closeWindow();
    }

    const declineChanged = () =>{
        // Revert to original data
        setCsvData(oldData);
        closeWindow();
    }

    useEffect(() => {
        console.log('NewCSVPopup useEffect triggered. newDataObj:', newDataObj);
        console.log('NewCSVPopup useEffect triggered. oldData:', oldData);

        if (!isPopupOpen || !newDataObj || !oldData) {
            setDataToShow([]); // Clear or handle initial/empty state
            return;
        }

        const { validRacers = [], removedRacers = [] } = newDataObj;

        // Create lookups for faster access
        const getRacerKey = (racer) => racer?.FIScode || racer?.USSAcode || `${racer?.firstname}-${racer?.lastname}`;

        const validRacersMap = new Map(validRacers.map(racer => [getRacerKey(racer), racer]));
        const removedRacersMap = new Map(removedRacers.map(item => [getRacerKey(item.racer), item])); // Store item {racer, reason}

        let updatedData = oldData.map(oldRow => {
            const key = getRacerKey(oldRow);
            let isRemoved = removedRacersMap.has(key);
            let isValid = validRacersMap.has(key);
            let validRowData = isValid ? validRacersMap.get(key) : null;

            // If not found initially, check if it was because FIS code was primary key and it was cleared
            if (!isRemoved && !isValid && oldRow.FIScode && oldRow.USSAcode) {
                const ussaKey = oldRow.USSAcode; // Key based on USSA code
                isValid = validRacersMap.has(ussaKey);
                if (isValid) {
                    validRowData = validRacersMap.get(ussaKey);
                }
            }

            let updatedRow = {};

            if (isRemoved) {
                // Racer was removed
                headers.forEach(header => {
                    const isUSSAContext = callerContext === 'USSA';
                    const isUSSACodeColumn = header === 'USSAcode';
                    const isFISContext = callerContext === 'FIS';
                    const isFISCodeColumn = header === 'FIScode';

                    // Apply red background ONLY if it's the correct context, the correct code column,
                    // AND the original row actually had a code value.
                    const hasOriginalCode = !!oldRow[header]; // Check if the old cell had a value
                    let cellSpecificClass = '';
                    if (isUSSAContext && isUSSACodeColumn && hasOriginalCode) {
                        cellSpecificClass = 'invalid-code-cell';
                    }
                    if (isFISContext && isFISCodeColumn && hasOriginalCode) {
                        cellSpecificClass = 'invalid-code-cell';
                    }

                    // Base class for all cells in a removed row
                    let baseRemovedClass = 'crossed-out';

                    // Add grey background if no specific red highlight is applied to this cell
                    if (!cellSpecificClass) {
                        baseRemovedClass += ' removed-row-background';
                    }

                    // Apply crossed-out style to the entire row by adding it to each cell's class list
                    updatedRow[header] = [oldRow[header] ?? '', `${baseRemovedClass} ${cellSpecificClass}`.trim()];
                });
            } else if (isValid) {
                // Racer is still valid, check for changes
                const newRow = validRowData; // Use the data retrieved using the correct key
                headers.forEach(header => {
                    const oldValue = oldRow[header] ?? '';
                    const newValue = newRow[header] ?? '';

                    // Special handling for cleared invalid FIS codes
                    if (header === 'FIScode' && oldValue !== '' && newValue === '') {
                        updatedRow[header] = [oldValue, 'crossed-out green'];
                    } else {
                        // Normal comparison for other cells or unchanged FIS codes
                        const newValueStr = String(newValue).toLowerCase();
                        const oldValueStr = String(oldValue).toLowerCase();

                        if (newValueStr !== oldValueStr && header !== 'rank') {
                            updatedRow[header] = [newValue, 'green'];
                        } else {
                            updatedRow[header] = [newValue, 'normal'];
                        }
                    }
                });
            } else {
                // Racer was in oldData but neither removed nor valid in newData (should not happen ideally)
                console.warn("Racer from oldData not found in valid or removed lists:", oldRow);
                headers.forEach(header => {
                    updatedRow[header] = [oldRow[header] ?? '', 'crossed-out']; // Default to crossed-out
                });
            }

            return updatedRow;
        });

        setDataToShow(updatedData);

    }, [newDataObj, oldData, headers, isPopupOpen, callerContext]); // Added dependencies

    // Determine if there's anything to display
    const hasValidNewData = newDataObj && (newDataObj.validRacers?.length > 0 || newDataObj.removedRacers?.length > 0);
    const hasOldData = oldData && oldData.length > 0;

    return(
        <>
        <div id="popup-csvData" 
            style={{display: isPopupOpen ? 'flex' : 'none'}}
        >
            <div className='button-bar'>
                <button onClick={acceptChanged} disabled={!newDataObj}>Accept</button> {/* Disable if no new data yet */}
                <button onClick={declineChanged}>Decline</button>
            </div>

            <div id='table-wraper'>
                {/* Check if the popup is open AND we have data to show */} 
                {isPopupOpen && (hasValidNewData || hasOldData) ? (
                    <table>
                        <thead>
                            <tr id='headers'>
                                {headers?.map((header, index) => (
                                <th key={`header-${header}-${index}`}>{header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                        {/* Use dataToShow which has styling info */} 
                        {dataToShow.map((row, idx) => (
                            <tr key={`row-${idx}`}>
                            {headers?.map((header, index) => (
                                <td 
                                    key={`cell-${idx}-${index}`}
                                    // Apply combined classes
                                    className={ 
                                        `${(header === 'firstname' || header === 'lastname' ? 'name' : '')} ${row[header]?.[1] || ''}`.trim()
                                    }
                                >
                                    {
                                        // Display value, handle potential Race Points calculation
                                        (row[header] && row[header][0] != null && String(row[header][0]) !== 'NaN') ?
                                            (
                                                (header === 'Race Points' && !isNaN(parseFloat(row[header]?.[0])) && !isNaN(parseFloat(headerInfo?.['penalty']))) ?
                                                    (parseFloat(row[header][0]) + parseFloat(headerInfo['penalty'])).toFixed(2) :
                                                    (String(row[header][0]))
                                            ) :
                                            (`\u00a0`) // Non-breaking space for empty cells
                                    }
                                </td>
                            ))}
                            </tr>
                        ))}
                        </tbody> 
                    </table>
                ) : (
                    // Show appropriate message if popup is open but no data
                    isPopupOpen && (
                        !oldData || oldData.length === 0 ? (
                            <h4>No Data Yet. Import a File.</h4>
                        ):(
                            <h4>No Valid Racers Found After Processing.</h4>
                        )
                    )
                )}
            </div>
        </div>
        {/* <div className='rendering-test'>rendering_test_csv_data</div> */}
        </>
    )
}

export default NewCSVData;